import React from 'react'
import { useI18n } from '../../i18n'
import ReadinessHero from './ReadinessHero'
import OperationalKpiCard from './OperationalKpiCard'
import { useAuditReadiness } from './useAuditReadiness'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'
import type { InformationSystemDetail } from '../../services/informationSystemsApi'
import type { OperationalSummaryRow } from '../../services/informationSystemOperationalApi'
import type { InformationSystemPanelContext } from './informationSystemPanelContext'

interface Props {
  system: InformationSystemDetail
  summaryRow?: OperationalSummaryRow | null
  panelContext: InformationSystemPanelContext
  onContextChange: (ctx: InformationSystemPanelContext) => void
}

export default function InformationSystemPanelIntelligence({
  system,
  summaryRow,
  onContextChange,
}: Props) {
  const { t } = useI18n()
  const row = summaryRow
  const audit = useAuditReadiness(
    row || {
      informationSystemId: system.id,
      name: system.name,
      applicableStandards: [],
      criticalDataCategories: [],
      readiness: { readinessStatus: 'UNKNOWN', controlCoveragePercent: null },
      topVulnerabilities: [],
      topFailedControls: [],
      topOpenRisks: [],
      linkedAssetsCount: system.linkedAssetsCount ?? 0,
    }
  )

  const vulnCrit = (row?.topVulnerabilities || []).filter(
    (v) => v.severity === 'CRITICAL' || v.severity === 'HIGH'
  ).length
  const failedControls = row?.failedControlsCount ?? row?.topFailedControls?.length ?? 0
  const openRisks = row?.openRisksCount ?? row?.topOpenRisks?.length ?? 0
  const remediation = row?.readiness?.remediationOpenCount ?? 0
  const assetsCount = row?.linkedAssetsCount ?? system.linkedAssetsCount ?? 0

  const readinessKpiValue =
    audit.percent != null ? `${audit.percent}%` : t('assets.postureRow.notAvailable', 'N/A')

  const quickNav = (kind: InformationSystemPanelContext['kind']) => {
    if (kind === 'fullPassport') return
    onContextChange({ kind, systemId: system.id } as InformationSystemPanelContext)
  }

  return (
    <aside className={styles.panelIntelligence}>
      <section className={styles.panelReadinessHero} aria-label={t('assets.auditReadiness.title', 'Audit readiness')}>
        <ReadinessHero
          variant="panel"
          model={audit}
          onClick={() => onContextChange({ kind: 'readiness', systemId: system.id })}
        />
      </section>

      <section className={styles.kpiGridPrimary} aria-label={t('assets.panel.kpi.primary', 'Primary operational KPIs')}>
        <OperationalKpiCard
          variant="primary"
          label={t('assets.panel.kpi.criticalVulnerabilities', 'Critical vulnerabilities')}
          value={String(vulnCrit)}
          onClick={() => onContextChange({ kind: 'vulnerability', systemId: system.id })}
        />
        <OperationalKpiCard
          variant="primary"
          label={t('assets.panel.kpi.failedControls', 'Failed controls')}
          value={String(failedControls)}
          onClick={() => onContextChange({ kind: 'control', systemId: system.id })}
        />
        <OperationalKpiCard
          variant="primary"
          label={t('assets.panel.kpi.risks', 'Open risks')}
          value={String(openRisks)}
          onClick={() => onContextChange({ kind: 'risk', systemId: system.id })}
        />
        <OperationalKpiCard
          variant="primary"
          label={t('assets.panel.kpi.auditReadiness', 'Audit readiness')}
          value={readinessKpiValue}
          onClick={() => onContextChange({ kind: 'readiness', systemId: system.id })}
        />
      </section>

      <section className={styles.kpiGridSecondary} aria-label={t('assets.panel.kpi.secondary', 'Secondary KPIs')}>
        <OperationalKpiCard
          variant="secondary"
          label={t('assets.panel.kpi.evidence', 'Evidence')}
          value="—"
          onClick={() => onContextChange({ kind: 'evidence', systemId: system.id })}
        />
        <OperationalKpiCard
          variant="secondary"
          label={t('assets.panel.kpi.remediation', 'Remediation')}
          value={String(remediation)}
          onClick={() => onContextChange({ kind: 'remediation', systemId: system.id })}
        />
        <OperationalKpiCard
          variant="secondary"
          label={t('assets.panel.kpi.assets', 'Assets')}
          value={String(assetsCount)}
          onClick={() => onContextChange({ kind: 'overview', systemId: system.id })}
        />
      </section>
    </aside>
  )
}
